import React, { useState, useEffect } from 'react';
import { apiCall } from '../../utils/apiUtils';
import { showToast } from '../../utils/toastUtils';
import { useAuth } from '../Auth/AuthContext';

const APIKeyManagement = ({ userId, userName, onClose }) => {
    const { user } = useAuth();
    const [apiKey, setApiKey] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showConfirmRegenerate, setShowConfirmRegenerate] = useState(false);
    const [showConfirmRevoke, setShowConfirmRevoke] = useState(false);

    // Determine target user ID (prop or current user)
    const targetUserId = userId || user.id;
    const targetUserName = userName || (userId ? 'User' : 'Your');

    useEffect(() => {
        fetchAPIKey();
    }, [targetUserId]);

    const fetchAPIKey = async () => {
        setLoading(true);
        try {
            // We need an endpoint to get the current key (masked) or status
            // But for security, maybe we only show it upon generation?
            // Or we can fetch the user details which includes api_key (if we added it to response)
            // We added api_key to UserResponse in server.py

            const response = await apiCall(`/api/users/detail/${targetUserId}`);
            // The endpoint might be /api/users/me or /api/users/{id}
            // Let's assume /api/users/{id} works for admin, and we need to check if it works for self
            // Actually, standard /api/users/{id} might not return sensitive data like api_key unless specifically requested or for admin
            // But we added it to UserResponse, so it should be there if the user has permission.

            // If we are the client, we can fetch our own profile?
            // Let's try fetching user details.

            // Note: In server.py, UserResponse includes api_key.
            // If the user is fetching themselves, they should see it.
            // If admin is fetching, they should see it.

            if (response && response.api_key) {
                setApiKey(response.api_key);
            } else {
                setApiKey(null);
            }
        } catch (error) {
            console.error('Error fetching API key:', error);
            // Don't show toast on load error to avoid spamming if just no key exists
        } finally {
            setLoading(false);
        }
    };

    const handleGenerate = async () => {
        setLoading(true);
        try {
            const response = await apiCall(`/api/users/${targetUserId}/api-key`, {
                method: 'POST'
            });
            setApiKey(response.api_key);
            showToast.success('API Key generated successfully');
            setShowConfirmRegenerate(false);
        } catch (error) {
            console.error('Error generating API key:', error);
            showToast.error('Failed to generate API Key');
        } finally {
            setLoading(false);
        }
    };

    const handleRevoke = async () => {
        setLoading(true);
        try {
            await apiCall(`/api/users/${targetUserId}/api-key`, {
                method: 'DELETE'
            });
            setApiKey(null);
            showToast.success('API Key revoked successfully');
            setShowConfirmRevoke(false);
        } catch (error) {
            console.error('Error revoking API key:', error);
            showToast.error('Failed to revoke API Key');
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = () => {
        if (apiKey) {
            navigator.clipboard.writeText(apiKey);
            showToast.success('API Key copied to clipboard');
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900">API Access Management</h3>
                    <p className="text-sm text-gray-600">Manage API keys for {targetUserName}</p>
                </div>
                {onClose && (
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                )}
            </div>

            <div className="space-y-6">
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Current API Key</label>
                    <div className="flex items-center space-x-2">
                        <div className="flex-1 bg-white border border-gray-300 rounded-md px-3 py-2 font-mono text-sm text-gray-800 break-all">
                            {apiKey || 'No API Key active'}
                        </div>
                        {apiKey && (
                            <button
                                onClick={copyToClipboard}
                                className="p-2 text-gray-500 hover:text-indigo-600 transition-colors"
                                title="Copy to clipboard"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                                </svg>
                            </button>
                        )}
                    </div>
                    {apiKey && (
                        <p className="mt-2 text-xs text-red-600 flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.502 0L4.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                            Keep this key secure. Do not share it publicly.
                        </p>
                    )}
                </div>

                <div className="flex space-x-4">
                    {!apiKey ? (
                        <button
                            onClick={handleGenerate}
                            disabled={loading}
                            className="btn-primary flex items-center"
                        >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Generate API Key
                        </button>
                    ) : (
                        <>
                            <button
                                onClick={() => setShowConfirmRegenerate(true)}
                                disabled={loading}
                                className="btn-secondary flex items-center"
                            >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                Regenerate Key
                            </button>
                            <button
                                onClick={() => setShowConfirmRevoke(true)}
                                disabled={loading}
                                className="btn-danger flex items-center"
                            >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Revoke Key
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Confirm Regenerate Modal */}
            {showConfirmRegenerate && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Regenerate API Key?</h3>
                        <p className="text-sm text-gray-600 mb-6">
                            This will invalidate the current API key immediately. Any applications using the old key will stop working.
                            Are you sure you want to proceed?
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setShowConfirmRegenerate(false)}
                                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleGenerate}
                                className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700"
                            >
                                Yes, Regenerate
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirm Revoke Modal */}
            {showConfirmRevoke && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Revoke API Key?</h3>
                        <p className="text-sm text-gray-600 mb-6">
                            This will delete the API key. The user will lose access to the API until a new key is generated.
                            Are you sure you want to proceed?
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setShowConfirmRevoke(false)}
                                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleRevoke}
                                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                            >
                                Yes, Revoke
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default APIKeyManagement;
